import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IconCalendar } from "@tabler/icons-react";
import DatePicker from "react-datepicker";

import UsageApi from "../../../api/usage";
import { UsageCreditsResponse } from "../../../interfaces";
import {
  getCurrentMonth,
  getCurrentWeek,
  getLastDays,
  getLastMonth,
  getTodayForFilter,
  parseDateForFilter,
  parseFilteredDates,
} from "../../../utils/moment";
import SkeletonText from "../../SkeletonText";
import datePickerStyles from "../../../theme/pickers-selects.module.css";
import styles from "./styles.module.css";
import "react-datepicker/dist/react-datepicker.css";

interface ILayoutProps {
  isMobile?: boolean;
}

const BILLING_CYCLE_FILTER = "billing_cycle";

const UsageDashboard: React.FC<ILayoutProps> = ({ isMobile = false }) => {
  const intl = useIntl();
  const { locale } = intl;
  const usageFilters = useMemo(
    () => [
      { value: BILLING_CYCLE_FILTER, label: "usage.filterBillingCycle" },
      { value: getTodayForFilter(), label: "common.today" },
      { value: getCurrentWeek(), label: "common.week" },
      { value: getLastDays({ days: 30 }), label: "common.30days" },
      { value: getCurrentMonth(), label: "common.thisMonth" },
      { value: getLastMonth(), label: "common.lastMonth" },
      { value: "custom", label: "common.custom" },
    ],
    []
  );
  const [commonFilter, setCommonFilter] = useState(BILLING_CYCLE_FILTER);
  const [appliedRange, setAppliedRange] = useState<{
    start?: string;
    end?: string;
  }>({});
  const [draftRange, setDraftRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);

  const { data: response, isLoading } = useQuery({
    queryKey: ["usageCredits", appliedRange.start || "", appliedRange.end || ""],
    queryFn: () => UsageApi.getCredits(appliedRange),
    refetchOnWindowFocus: false,
  });

  const usageData = (response?.data || null) as UsageCreditsResponse | null;
  const hasError = Boolean(response?.err);
  const series = usageData?.series || [];
  const hasSeries = series.length > 0;

  const categories = series.map((item) => item.date);
  const points = series.map((item) => item.billableCredits);

  const onChangeCommonFilter = (filter: string) => {
    if (filter === BILLING_CYCLE_FILTER) {
      setCommonFilter(BILLING_CYCLE_FILTER);
      setAppliedRange({});
      setIsCustomRangeModalOpen(false);
      return;
    }

    if (filter === "custom") {
      const { start, end } = getCurrentAppliedDates({
        commonFilter,
        appliedRange,
        usageRange: usageData?.range,
      });
      setDraftRange({ start, end });
      setIsCustomRangeModalOpen(true);
      return;
    }

    const [start, end] = parseFilteredDates(filter);
    setCommonFilter(filter);
    setAppliedRange({
      start: parseDateForFilter(start),
      end: parseDateForFilter(end),
    });
  };

  const onChangeDraftDates = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setDraftRange({ start: start || null, end: end || null });
  };

  const onCancelCustomRange = () => {
    setIsCustomRangeModalOpen(false);
  };

  const onApplyCustomRange = () => {
    const { start, end } = draftRange;
    if (!start || !end) return;

    setAppliedRange({
      start: parseDateForFilter(start),
      end: parseDateForFilter(end),
    });
    setCommonFilter("custom");
    setIsCustomRangeModalOpen(false);
  };

  const canApplyCustomRange = Boolean(draftRange.start && draftRange.end);

  const activeRangeStart =
    commonFilter === BILLING_CYCLE_FILTER
      ? usageData?.range?.start
      : appliedRange.start;
  const activeRangeEnd =
    commonFilter === BILLING_CYCLE_FILTER ? usageData?.range?.end : appliedRange.end;
  const activeRangeText =
    activeRangeStart && activeRangeEnd
      ? `${formatShortDate(intl, activeRangeStart)} - ${formatShortDate(
          intl,
          activeRangeEnd
        )}`
      : "-";

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
    },
    title: { text: "" },
    xAxis: {
      categories,
      lineColor: "var(--visito-border-color)",
      tickColor: "var(--visito-border-color)",
      labels: {
        style: {
          color: "var(--ion-color-medium)",
          fontSize: "12px",
        },
        formatter: function () {
          if (typeof this.value !== "string") return "";
          return formatShortDate(intl, this.value);
        },
      },
    },
    yAxis: {
      allowDecimals: false,
      min: 0,
      gridLineColor: "var(--visito-chat-input-border)",
      lineColor: "var(--visito-border-color)",
      title: {
        text: intl.formatMessage({ id: "usage.axisCredits" }),
        style: {
          color: "var(--ion-color-medium)",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "var(--ion-color-medium)",
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      shared: false,
      borderColor: "var(--visito-border-color)",
      backgroundColor: "var(--visito-background-color)",
      style: {
        color: "var(--ion-color-dark)",
      },
      formatter: function () {
        const date =
          typeof this.x === "string" ? formatLongDate(intl, this.x) : "";
        const value = Number(this.y || 0);
        return `${date}<br/><b>${intl.formatNumber(value)}</b> ${intl.formatMessage({
          id: "usage.creditsUsed",
        })}`;
      },
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        borderRadius: 4,
      },
      series: {
        animation: {
          duration: 700,
        },
      },
    },
    series: [
      {
        type: "column",
        name: intl.formatMessage({ id: "usage.seriesBillable" }),
        data: points,
        color: "var(--visito-color-cool)",
      },
    ],
    legend: { enabled: false },
    credits: { enabled: false },
  };

  if (isLoading) {
    return <SkeletonText rows={6} />;
  }

  if (hasError || !usageData) {
    return (
      <div className={styles.stateText}>
        <FormattedMessage id="usage.error" />
      </div>
    );
  }

  if (!hasSeries) {
    return (
      <div className={styles.stateText}>
        <FormattedMessage id="usage.empty" />
      </div>
    );
  }

  const totalBillable = intl.formatNumber(usageData.summary.totalBillable || 0);
  const avgPerDay = intl.formatNumber(usageData.summary.avgPerDay || 0, {
    maximumFractionDigits: 1,
  });
  const peakDayCount = intl.formatNumber(usageData.summary.peakDay?.count || 0);
  const peakDayDate = usageData.summary.peakDay?.date
    ? formatLongDate(intl, usageData.summary.peakDay.date)
    : "-";

  if (isMobile) {
    return (
      <div className={styles.container}>
        <IonList inset={true} className={styles.mobileSummary}>
          <IonItem>
            <IonLabel>
              <FormattedMessage id="usage.kpiTotal" />
            </IonLabel>
            <div slot="end">
              <strong>{totalBillable}</strong>
            </div>
          </IonItem>
          <IonItem>
            <IonLabel>
              <FormattedMessage id="usage.kpiAverage" />
            </IonLabel>
            <div slot="end">
              <strong>{avgPerDay}</strong>
            </div>
          </IonItem>
          <IonItem>
            <IonLabel>
              <FormattedMessage id="usage.kpiPeak" />
            </IonLabel>
            <div slot="end" className="text-end">
              <strong>{peakDayCount}</strong>
              <div className={styles.meta}>{peakDayDate}</div>
            </div>
          </IonItem>
        </IonList>

        <div className={styles.chartCard}>
          <div className="chart-container">
            <div className={styles.chartWrap}>
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ className: "chart-div" }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.filtersRow}>
        <div className={`${datePickerStyles.filtersContainer} ${styles.filters}`}>
          <IonSelect
            labelPlacement="stacked"
            className={datePickerStyles.commonFilterSelect}
            value={commonFilter}
            onIonChange={(e) => onChangeCommonFilter(e.target.value)}
          >
            {usageFilters.map((filter) => (
              <IonSelectOption key={filter.value} value={filter.value}>
                <FormattedMessage id={filter.label} />
              </IonSelectOption>
            ))}
          </IonSelect>
          <button
            type="button"
            className={`${datePickerStyles.dateWrapper} ${styles.rangeButton}`}
            onClick={() => {
              const { start, end } = getCurrentAppliedDates({
                commonFilter,
                appliedRange,
                usageRange: usageData?.range,
              });
              setDraftRange({ start, end });
              setIsCustomRangeModalOpen(true);
            }}
          >
            <IconCalendar size={17} />
            <span className={styles.rangeText}>{activeRangeText}</span>
          </button>
        </div>
      </div>

      <IonModal
        isOpen={isCustomRangeModalOpen}
        onDidDismiss={onCancelCustomRange}
        className={styles.customRangeModal}
      >
        <div className={styles.customRangeModalBody}>
          <h4 className={styles.customRangeTitle}>
            <FormattedMessage id="usage.customRangeTitle" />
          </h4>
          <DatePicker
            selectsRange
            inline
            locale={locale}
            startDate={draftRange.start}
            endDate={draftRange.end}
            onChange={onChangeDraftDates}
            monthsShown={isMobile ? 1 : 2}
          />
          <div className={styles.customRangeActions}>
            <button
              type="button"
              className="btn btn-visito-light w-100"
              onClick={onCancelCustomRange}
            >
              <FormattedMessage id="common.cancel" />
            </button>
            <button
              type="button"
              className="btn btn-visito-primary w-100"
              disabled={!canApplyCustomRange}
              onClick={onApplyCustomRange}
            >
              <FormattedMessage id="common.apply" />
            </button>
          </div>
        </div>
      </IonModal>

      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>
            <FormattedMessage id="usage.kpiTotal" />
          </div>
          <div className={styles.kpiValue}>{totalBillable}</div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>
            <FormattedMessage id="usage.kpiAverage" />
          </div>
          <div className={styles.kpiValue}>{avgPerDay}</div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>
            <FormattedMessage id="usage.kpiPeak" />
          </div>
          <div className={styles.kpiValue}>{peakDayCount}</div>
          <div className={styles.meta}>{peakDayDate}</div>
        </div>
      </div>

      <div className={styles.chartCard}>
        <div className="chart-container">
          <div className={styles.chartWrap}>
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              containerProps={{ className: "chart-div" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const formatShortDate = (intl: ReturnType<typeof useIntl>, value: string) => {
  const parsed = parseIsoDay(value);
  if (!parsed) return value;
  return intl.formatDate(parsed, { month: "short", day: "numeric", timeZone: "UTC" });
};

const formatLongDate = (intl: ReturnType<typeof useIntl>, value: string) => {
  const parsed = parseIsoDay(value);
  if (!parsed) return value;
  return intl.formatDate(parsed, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const parseIsoDay = (value: string): Date | null => {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const getCurrentAppliedDates = ({
  commonFilter,
  appliedRange,
  usageRange,
}: {
  commonFilter: string;
  appliedRange: { start?: string; end?: string };
  usageRange?: { start: string; end: string };
}) => {
  if (commonFilter === BILLING_CYCLE_FILTER && usageRange?.start && usageRange?.end) {
    return {
      start: parseIsoDay(usageRange.start),
      end: parseIsoDay(usageRange.end),
    };
  }

  if (appliedRange.start && appliedRange.end) {
    return {
      start: parseIsoDay(appliedRange.start),
      end: parseIsoDay(appliedRange.end),
    };
  }

  return { start: null, end: null };
};

export default UsageDashboard;
